import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { ApartmentJobWork } from '../components/job/ApartmentJobWork'
import { JobTaskChecklist } from '../components/job/JobTaskChecklist'
import { supabase } from '../lib/supabase'
import { isLikelyOfflineError } from '../lib/offlinePhotoSync'
import {
  cacheJobSummary,
  readCachedJobSummary,
  type CachedJobSummary,
} from '../lib/offlineJobChecklistCache'

export function JobChecklist() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [job, setJob] = useState<CachedJobSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [offline, setOffline] = useState(false)

  const locationState =
    location.state && typeof location.state === 'object'
      ? (location.state as { from?: unknown; photoMode?: unknown })
      : null
  const fromState = locationState?.from ? String(locationState.from) : ''
  const startPhotoMode =
    locationState?.photoMode === true ||
    new URLSearchParams(location.search).get('photo') === '1'

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      setOffline(false)
      const cached = readCachedJobSummary(id)
      if (cached && !cancelled) {
        setJob(cached)
      }
      try {
        const { data, error: fetchError } = await supabase
          .from('jobs')
          .select('id, job_number, title, description, service_type, contract_id, visit_group_id, is_recurring')
          .eq('id', id)
          .single()
        if (fetchError) throw fetchError
        if (!cancelled) {
          const row = data as CachedJobSummary
          setJob(row)
          cacheJobSummary(row)
        }
      } catch (e) {
        if (!cancelled) {
          if (cached) {
            setJob(cached)
            setOffline(isLikelyOfflineError(e))
            setError(null)
          } else {
            setJob(null)
            setError(e instanceof Error ? e.message : 'Failed to load job checklist')
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    if (fromState) {
      navigate(fromState)
      return
    }
    if (id) navigate(`/jobs/details/${id}`)
    else navigate('/jobs')
  }

  const handleCompleteJob = () => {
    if (!id) return
    navigate(`/jobs/details/${id}`, {
      state: {
        from: fromState || `/jobs/details/${id}/checklist`,
        openCompletion: true,
      },
      replace: true,
    })
  }

  if (loading) {
    return (
      <MobilePage>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Checklist"
        subtitle={job ? `#${job.job_number} · ${job.title}` : undefined}
        onBack={goBack}
        backLabel="Back to job"
        sticky
      />

      {error || !job || !id ? (
        <div className={`${mobile.surface} p-4`}>
          <p className="text-sm text-gray-500">{error || 'Job not found.'}</p>
        </div>
      ) : (
        <div className={`${mobile.surface} p-4 space-y-4`}>
          {offline ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Offline — showing saved checklist. Photos queue until you reconnect.
            </p>
          ) : null}
          {job.description ? (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Work to be done</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.description}</p>
            </div>
          ) : null}
          <ApartmentJobWork jobId={id} serviceType={job.service_type} />
          <JobTaskChecklist
            jobId={id}
            contractId={job.contract_id}
            serviceType={job.service_type}
            showOneOffBadges={!!job.visit_group_id || !!job.is_recurring}
            generalOnly
            flowOnly
            startPhotoMode={startPhotoMode}
            onCompleteJob={handleCompleteJob}
          />
        </div>
      )}
    </MobilePage>
  )
}
